import React from 'react';
import { useTranslation } from 'react-i18next';
import manifest from '../../seo/shotManifest.json';
import styles from './styles.module.css';

const SUPPORTED = ['en', 'he', 'ar'];

/**
 * ImageObject JSON-LD for a feature's shots, in one language.
 *
 * Exported for the PAGE to pass into <SEO jsonLd={...}>. Emitting it from
 * inside this section component would mean a second Helmet instance competing
 * with the page's, which is how duplicate or missing meta happens.
 */
export function shotImageJsonLd(featureKey, lang) {
  const shots = manifest[featureKey];
  if (!shots || !shots.length) return [];
  const l = SUPPORTED.includes(lang) ? lang : 'en';
  return shots.map((shot) => ({
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    contentUrl: `https://stylon.co/shots/${l}/${shot.slug}.webp`,
    caption: (shot.alt && (shot.alt[l] || shot.alt.en)) || '',
    representativeOfPage: false,
  }));
}

/** Intrinsic size of the built WebP, so the strip reserves its space. */
const DIMS = { d: { width: 1200, height: 750 }, p: { width: 420, height: 860 } };

/**
 * Real portal screenshots, in the reader's own language.
 *
 * Built by scripts/buildShots.js out of the operator-guide captures. A feature
 * with no manifest entry renders NOTHING rather than an empty frame — four
 * pages legitimately have no capture yet and they ship on their SVG demo.
 */
const ShotStrip = ({ featureKey, color = '#F36523' }) => {
  const { t, i18n } = useTranslation();
  const shots = manifest[featureKey];
  if (!shots || !shots.length) return null;

  const base = (i18n.language || 'en').split('-')[0];
  const lang = SUPPORTED.includes(base) ? base : 'en';

  return (
    <section className={styles.section}>
      <div className="container">
        <h2 className={styles.title} data-aos="fade-up">
          {t('features.common.shotsTitle', 'From inside Stylon')}
        </h2>

        <div className={styles.strip}>
          {shots.map((shot, i) => {
            const dims = DIMS[shot.device] || DIMS.d;
            return (
              <figure
                key={shot.slug}
                role="figure"
                className={`${styles.figure} ${shot.device === 'p' ? styles.phone : styles.desktop}`}
                data-aos="fade-up"
                data-aos-delay={i * 90}
              >
                <div
                  className={styles.frame}
                  style={{ boxShadow: `0 30px 60px -30px ${color}55` }}
                >
                  <img
                    src={`/shots/${lang}/${shot.slug}.webp`}
                    alt={(shot.alt && (shot.alt[lang] || shot.alt.en)) || ''}
                    width={dims.width}
                    height={dims.height}
                    loading="lazy"
                    decoding="async"
                    className={styles.img}
                  />
                </div>
                <figcaption className={styles.caption}>
                  {(shot.alt && (shot.alt[lang] || shot.alt.en)) || ''}
                </figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ShotStrip;
